import { LitElement } from "lit"
import { customElement, property } from "lit/decorators.js"
import mapboxgl from "mapbox-gl"
import type { MapControl, MapControlPosition } from "../map-control.ts"
import { applyDepth, type MapStyle, STYLE_STORAGE_KEY, STYLE_URLS } from "../map-styles.ts"
import { MAP_EVENT, mapEvent } from "@/lib/map/types.ts"

const STYLE_LABELS: Record<MapStyle, string> = {
  terrain: "Terrain",
  satellite: "Satellite",
}

const STYLE_ICONS: Record<MapStyle, string> = {
  terrain:
    `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M1 13L5.5 6.5L8.5 9.5L11 5L15 13H1Z"/></svg>`,
  satellite:
    `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="6"/><ellipse cx="8" cy="8" rx="2.5" ry="6"/><line x1="2" y1="8" x2="14" y2="8"/></svg>`,
}

const STYLES: MapStyle[] = ["terrain", "satellite"]

@customElement("map-style-switcher")
export class MapStyleSwitcher extends LitElement implements MapControl {
  // No shadow DOM — we render entirely inside Mapbox's control container
  override createRenderRoot() {
    return this
  }

  @property({ attribute: "position" })
  accessor position: MapControlPosition = "top-left"

  private _map: mapboxgl.Map | null = null
  private _container: HTMLElement | null = null
  private _activeStyle: MapStyle = "terrain"

  onAdd(map: mapboxgl.Map): HTMLElement {
    this._map = map

    // Determine initial active style
    try {
      const stored = localStorage.getItem(STYLE_STORAGE_KEY) as MapStyle | null
      if (stored && stored in STYLE_URLS) {
        this._activeStyle = stored
      }
    } catch {
      // ignore
    }

    this._container = document.createElement("div")
    this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group"
    this._container.style.cssText = `
      display: flex; flex-direction: column; border-radius: 4px; overflow: hidden;
      box-shadow: 0 0 0 2px rgba(0,0,0,0.1);
    `

    this._render()
    return this._container
  }

  onRemove(_map: mapboxgl.Map): void {
    this._container?.remove()
    this._container = null
    this._map = null
  }

  private _render() {
    if (!this._container) return
    this._container.innerHTML = ""

    for (const style of STYLES) {
      const btn = document.createElement("button")
      btn.innerHTML = STYLE_ICONS[style]
      btn.style.cssText = `
        width: 29px; height: 29px;
        display: flex; align-items: center; justify-content: center;
        border: none; cursor: pointer; transition: background 0.15s;
        background: ${
        style === this._activeStyle ? "var(--color-link, #3b82f6)" : "var(--color-surface, #fff)"
      };
        color: ${
        style === this._activeStyle ? "var(--color-surface, #fff)" : "var(--color-link, #374151)"
      };
      `
      btn.setAttribute("type", "button")
      btn.setAttribute("title", STYLE_LABELS[style])
      btn.addEventListener("click", () => this._selectStyle(style))
      this._container.appendChild(btn)
    }
  }

  private _selectStyle(style: MapStyle) {
    if (!this._map) return
    this._activeStyle = style

    // Persist to localStorage
    try {
      localStorage.setItem(STYLE_STORAGE_KEY, style)
    } catch {
      // ignore
    }

    // Update button appearance immediately
    this._render()

    // Change map style
    this._map.setStyle(STYLE_URLS[style])

    // `setStyle` clears sources, layers, terrain and fog alike, so everything
    // the app put on the map has to go back on once the new style is up.
    this._map.once("style.load", () => {
      if (!this._map) return

      // The host owns the current `lightPreset`, which the depth palette keys
      // off, so let it re-add the stack. The direct call is the fallback for a
      // host that predates `readdDepth` — `day` is the right guess there.
      const host = this.closest("trip-map, globe-view") as
        | (HTMLElement & { readdRoute?(): void; readdDepth?(style: MapStyle): void })
        | null
      if (host?.readdDepth) host.readdDepth(style)
      else applyDepth(this._map, style)

      host?.readdRoute?.()

      // Emit style-change event on the host map element
      const mapEl = this.closest("trip-map, globe-view")
      mapEl?.dispatchEvent(mapEvent(MAP_EVENT.styleChange, { style }))
    })
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "map-style-switcher": MapStyleSwitcher
  }
}
